import type { RouteRecordRaw } from 'vue-router'

import { useGetStartedAccess } from './composables/useGetStartedAccess'
import { openOnboarding } from './flows/openOnboarding'
import { registerGlobalSurfaceMessages } from './i18n'
import { ONBOARDING_ROUTE_NAME } from './models/onboarding.types'
import { isOnboardingStepKey } from './service/onboardingSteps'

import { useWorkspace } from '@/features/account'
import { registerIntegrationsMessages } from '@/features/integrations'
import { DEFAULT_LOCALE, SUPPORTED_LOCALES, i18n, type SupportedLocale } from '@/shared/i18n'

function activeLocale(): SupportedLocale {
  const locale = i18n.global.locale.value
  return (SUPPORTED_LOCALES as readonly string[]).includes(locale)
    ? (locale as SupportedLocale)
    : DEFAULT_LOCALE
}

/**
 * Contract 29 §11 (amended 2026-10-22) — onboarding is a TAKEOVER over the app, not a page: it has
 * to appear on its own conditions and the user must not be able to navigate around it
 * (`OnboardingHost`). This record keeps the old address working for bookmarks, legacy redirects and
 * any caller still pushing `{ name: 'Onboarding' }`: it opens the takeover at the asked step and
 * lands on Summary underneath. Whether the run is mandatory is the gate's call, not the URL's.
 */
export const onboardingRoutes: RouteRecordRaw[] = [
  {
    path: '/onboarding/:step(who-brand|found-brand|business-type|connect|one-last-thing|workspace|settings|authentications|connections)?',
    name: ONBOARDING_ROUTE_NAME,
    // Never rendered: `beforeEnter` always redirects. A component is still required by the record
    // type for a route with a guard, so it points at nothing heavier than an empty render.
    component: { render: () => null },
    meta: {
      requiresAuth: true,
      // `mcpAllowed` (ORP-21656): the takeover covers the MCP surface too.
      mcpAllowed: true,
      titleKey: 'globalSurface.onboarding.title',
    },
    beforeEnter: (to) => {
      const param = Array.isArray(to.params.step) ? to.params.step[0] : to.params.step
      openOnboarding(isOnboardingStepKey(param) ? param : undefined)
      return { name: 'Summary', replace: true }
    },
  },
]

export const getStartedRoutes: RouteRecordRaw[] = [
  {
    path: 'get-started',
    name: 'GetStarted',
    component: () => import('./components/get-started/GetStartedPage.vue'),
    meta: { titleKey: 'globalSurface.getStarted.title', navKey: 'getStarted' },
    beforeEnter: async () => {
      // A cold deep link resolves before account boot has published the workspace list; the page
      // re-checks the same answer once it lands (legacy `router/index.js:353-363`).
      if (useWorkspace().workspaces.value.length > 0) {
        const access = useGetStartedAccess()
        await access.ensureLoaded()
        if (!access.available.value) return { name: 'Summary' }
      }
      const locale = activeLocale()
      await Promise.all([
        registerGlobalSurfaceMessages(locale),
        registerIntegrationsMessages(locale),
      ])
      return true
    },
  },
]
